/**
 * Parses a route path string into its pathname and query parameters.
 *
 * - When working with router state (like `router.options.history.state.back`), we need to explicitly
 *   handle query parameters rather than just using the path string. This is because:
 *   - The path string alone doesn't preserve pagination state
 *   - Direct path manipulation can lose important query parameters
 *   - `useStorePagination` expects query parameters to be properly structured on the Vue Router
 *
 * @param {string} routePath - The full route path string to parse, including query parameters
 * @returns {{ path: string; query: Record<string, any> }} An object containing:
 *   - path: The clean pathname without query parameters
 *   - query: An object containing all query parameters, properly structured for use with `useStorePagination`
 *
 * @example
 *  const { path, query } = useParsePathAndQuery(router.options.history.state.back);
 *  router.push({ path, query });
 *  Now we can properly preserve pagination/query params state when navigating
 */
import { set } from 'lodash-es';

export default (routePath: string): { path: string; query: Record<string, any> } => {
  // Create a URL object from the provided string
  const urlObj = new URL(routePath, window.location.origin);

  const path = urlObj.pathname;

  const query = {};

  urlObj.searchParams.forEach((value, key) => {
    set(query, key, value);
  });
  return { path, query };
};
